import React from "react";
import { Helmet } from "react-helmet-async";
import { PrimaryButton, SecondaryButton, OutlineButton } from "../../components/ui/StandardButton";
import HeroHeading from "../../components/ui/HeroHeading";
import InteractiveCard from "../../components/InteractiveCard";
import { useTranslation } from "react-i18next";
import { CheckCircle, Star, Mountain, Users, Moon, Coffee, Gift, Clock, Calendar } from "lucide-react";
import { colors } from "../../styles/colors";
import { formatEUR } from "../../lib/utils";
import { useResponsive } from "../../hooks/useResponsive";
import LazyVideo from "../../components/LazyVideo";

const ICONS = {
  mountain: Mountain,
  users: Users,
  moon: Moon,
  coffee: Coffee,
  gift: Gift,
  clock: Clock,
  calendar: Calendar,
  star: Star,
};

function getDefaultParagraphs(t) {
  return [
    t('pages.laplandHoliday.intro.paragraphs.0'),
    t('pages.laplandHoliday.intro.paragraphs.1'),
  ];
}

function getDefaultFeatures(t) {
  return [
    {
      icon: "mountain",
      title: t('pages.laplandHoliday.intro.features.0.title'),
      description: t('pages.laplandHoliday.intro.features.0.description'),
    },
    {
      icon: "users",
      title: t('pages.laplandHoliday.intro.features.1.title'),
      description: t('pages.laplandHoliday.intro.features.1.description'),
    },
    {
      icon: "moon",
      title: t('pages.laplandHoliday.intro.features.2.title'),
      description: t('pages.laplandHoliday.intro.features.2.description'),
    },
    {
      icon: "coffee",
      title: t('pages.laplandHoliday.intro.features.3.title'),
      description: t('pages.laplandHoliday.intro.features.3.description'),
    },
  ];
}

function getDefaultSevenDayIncludes(t) {
  return [
    t('pages.laplandHoliday.featured.package.includes.0'),
    t('pages.laplandHoliday.featured.package.includes.1'),
    t('pages.laplandHoliday.featured.package.includes.2'),
    t('pages.laplandHoliday.featured.package.includes.3'),
    t('pages.laplandHoliday.featured.package.includes.4'),
    t('pages.laplandHoliday.featured.package.includes.5'),
    t('pages.laplandHoliday.featured.package.includes.6'),
    t('pages.laplandHoliday.featured.package.includes.7'),
    t('pages.laplandHoliday.featured.package.includes.8'),
    t('pages.laplandHoliday.featured.package.includes.9'),
    t('pages.laplandHoliday.featured.package.includes.10'),
    t('pages.laplandHoliday.featured.package.includes.11'),
    t('pages.laplandHoliday.featured.package.includes.12'),
    t('pages.laplandHoliday.featured.package.includes.13'),
  ];
}

function getDefaultFiveDayIncludes(t) {
  return [
    t('pages.laplandHoliday.fiveDay.package.includes.0'),
    t('pages.laplandHoliday.fiveDay.package.includes.1'),
    t('pages.laplandHoliday.fiveDay.package.includes.2'),
    t('pages.laplandHoliday.fiveDay.package.includes.3'),
    t('pages.laplandHoliday.fiveDay.package.includes.4'),
    t('pages.laplandHoliday.fiveDay.package.includes.5'),
    t('pages.laplandHoliday.fiveDay.package.includes.6'),
    t('pages.laplandHoliday.fiveDay.package.includes.7'),
    t('pages.laplandHoliday.fiveDay.package.includes.8'),
    t('pages.laplandHoliday.fiveDay.package.includes.9'),
    t('pages.laplandHoliday.fiveDay.package.includes.10'),
    t('pages.laplandHoliday.fiveDay.package.includes.11'),
  ];
}

function getDefaultOneDayIncludes(t) {
  return [
    t('pages.laplandHoliday.oneDay.package.includes.0'),
    t('pages.laplandHoliday.oneDay.package.includes.1'),
    t('pages.laplandHoliday.oneDay.package.includes.2'),
    t('pages.laplandHoliday.oneDay.package.includes.3'),
    t('pages.laplandHoliday.oneDay.package.includes.4'),
    t('pages.laplandHoliday.oneDay.package.includes.5'),
    t('pages.laplandHoliday.oneDay.package.includes.6'),
  ];
}

function getDefaultThreeDayIncludes(t) {
  return [
    t('pages.laplandHoliday.threeDay.package.includes.0'),
    t('pages.laplandHoliday.threeDay.package.includes.1'),
    t('pages.laplandHoliday.threeDay.package.includes.2'),
    t('pages.laplandHoliday.threeDay.package.includes.3'),
    t('pages.laplandHoliday.threeDay.package.includes.4'),
    t('pages.laplandHoliday.threeDay.package.includes.5'),
    t('pages.laplandHoliday.threeDay.package.includes.6'),
  ];
}

const DEFAULT_THREE_DAY_ITINERARY = [];

const DEFAULT_THREE_DAY_ADDONS = [];

function getDefaultGalleryCaptions(t) {
  return [
    t('pages.laplandHoliday.featured.galleryCaptions.0'),
    t('pages.laplandHoliday.featured.galleryCaptions.1'),
    t('pages.laplandHoliday.featured.galleryCaptions.2'),
  ];
}

const formatPrice = (value, fallback) =>
  typeof value === "number" ? formatEUR(value) : value || fallback;

const normalizeList = (value, fallback = []) => {
  if (Array.isArray(value) && value.length) return value;
  if (value && typeof value === "object") {
    const entries = Object.values(value).filter(Boolean);
    if (entries.length) return entries;
  }
  if (typeof value === "string") {
    const parts = value
      .split(/,|\r?\n/)
      .map((item) => item.trim())
      .filter(Boolean);
    if (parts.length) return parts;
  }

  return fallback;
};

const LaplandHolidayPackages = () => {
  const siteUrl = process.env.REACT_APP_SITE_URL || "https://coldexperience.se";
  const { t, i18n } = useTranslation();
  const defaultSevenDayIncludes = getDefaultSevenDayIncludes(t);
  const defaultFiveDayIncludes = getDefaultFiveDayIncludes(t);
  const defaultOneDayIncludes = getDefaultOneDayIncludes(t);
  const defaultThreeDayIncludes = getDefaultThreeDayIncludes(t);
  const defaultGalleryCaptions = getDefaultGalleryCaptions(t);
  const { isMobile } = useResponsive();
  const langPrefix = `/${i18n.language || "en"}`;
  const content = t("pages.laplandHoliday", { returnObjects: true }) || {};
  const heroSpacing = isMobile ? "px-4 pt-24 pb-16" : "px-4 sm:px-6 lg:px-8 pt-32 pb-24";

  const hero = content.hero || {};
  // CMS-managed media with hardcoded fallbacks
  const heroVideoSrc = hero.media?.videoSrc || '/optimized_videos/coldexperience1-opt.mp4';
  const heroPoster = hero.media?.poster || '/images/Nya_bilder/IMG_6698.webp';
  const introMedia = content.intro?.media || {};
  const introVideoSrc = introMedia.videoSrc || '/optimized_videos/norrsken1-opt.mp4';
  const introPoster = introMedia.poster || '/og/holiday-packages.jpg';
  const fallbackHeroTitle = hero.title || "Winter Package";
  const fallbackHeroParts = (() => {
    if (!hero.title) return { primary: "", accent: "" };
    const words = hero.title.trim().split(/\s+/);
    if (words.length <= 1) return { primary: "", accent: "" };
    return {
      primary: words.slice(0, -1).join(" "),
      accent: words.slice(-1).join(" "),
    };
  })();
  const heroTitlePrimary = hero.titlePrimary ?? fallbackHeroParts.primary;
  const heroTitleAccent = hero.titleAccent ?? fallbackHeroParts.accent;
  const introParagraphs = content.intro?.paragraphs?.length
    ? content.intro.paragraphs
    : getDefaultParagraphs(t);
  const introFeatures = content.intro?.features?.length
    ? content.intro.features
    : getDefaultFeatures(t);

  const featured = content.featured || {};
  const featuredPackage = featured.package || {};
  const featuredIncludes = normalizeList(
    featuredPackage.includes,
    defaultSevenDayIncludes
  );
  const featuredGallery = normalizeList(
    featured.galleryCaptions,
    defaultGalleryCaptions
  );

  const fiveDay = content.fiveDay || {};
  const fiveDayPackage = fiveDay.package || {};
  const fiveDayIncludes = normalizeList(
    fiveDayPackage.includes,
    defaultFiveDayIncludes
  );

  const threeDay = content.threeDay || {};
  const threeDayPackage = threeDay.package || {};
  const threeDayIncludes = normalizeList(
    threeDayPackage.includes,
    defaultThreeDayIncludes
  );
  const threeDayItinerary = normalizeList(
    threeDayPackage.itinerary,
    DEFAULT_THREE_DAY_ITINERARY
  );
  const threeDayAddOns = normalizeList(
    threeDayPackage.addOns,
    DEFAULT_THREE_DAY_ADDONS
  );

  const oneDay = content.oneDay || {};
  const oneDayPackage = oneDay.package || {};
  const oneDayIncludes = normalizeList(
    oneDayPackage.includes,
    defaultOneDayIncludes
  );

  const cta = content.cta || {};

  const renderFeatureIcon = (iconKey) => {
    const Icon = ICONS[iconKey] || Star;
    return <Icon className="h-6 w-6" />;
  };

  return (
    <div className="min-h-screen">
      <Helmet>
  <title>{content.metaTitle || t('pages.laplandHoliday.meta.title')}</title>
        <meta
          name="description"
          content={content.metaDescription || t('pages.laplandHoliday.meta.description')}
        />
        <meta
          property="og:title"
          content={content.metaTitle || t('pages.laplandHoliday.meta.ogTitle')}
        />
        <meta
          property="og:description"
          content={content.metaDescription || t('pages.laplandHoliday.meta.ogDescription')}
        />
  <meta property="og:image" content={t('pages.laplandHoliday.meta.ogImage') || `${siteUrl}/og/packages.jpg`} />
  <meta name="twitter:image" content={t('pages.laplandHoliday.meta.twitterImage') || `${siteUrl}/og/packages.jpg`} />
      </Helmet>

      <section
        data-hero="true"
        data-cms-section="packages"
        data-cms-page="packages"
        id="packages-hero"
        className="relative min-h-screen flex items-center justify-center text-center overflow-hidden"
        style={{ background: colors.gradients.hero }}
      >
        <div className="absolute inset-0">
          <LazyVideo
            src={heroVideoSrc}
            poster={heroPoster}
            autoPlay
            loop
            muted
            playsInline
            preload="metadata"
            className="absolute inset-0"
            videoClassName="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: colors.gradients.hero }}></div>
        </div>

        <div className={`relative z-10 w-full ${heroSpacing}`}>
          <div className="max-w-4xl mx-auto" style={{ color: colors.text.primary }}>
          <HeroHeading
            primary={heroTitlePrimary}
            accent={heroTitleAccent}
            title={!heroTitlePrimary && !heroTitleAccent ? fallbackHeroTitle : undefined}
            className="text-5xl md:text-6xl font-bold mb-6"
          />
          <p className="text-xl md:text-2xl mb-8 leading-relaxed" style={{ color: colors.text.primary }}>
            {hero.subtitle || t('pages.laplandHoliday.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <PrimaryButton to={`${langPrefix}/book`} size="large">
              {hero.primaryCta || t('pages.laplandHoliday.hero.primaryCta')}
            </PrimaryButton>
            <SecondaryButton to={`${langPrefix}/contact`} size="large">
              {hero.secondaryCta || t('pages.laplandHoliday.hero.secondaryCta')}
            </SecondaryButton>
          </div>
          </div>
        </div>
      </section>

      <section data-cms-section="packagesIntro" data-cms-page="packages" id="packages-intro" className="py-16" style={{ background: colors.gradients.heroSolid }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid gap-12 lg:grid-cols-2 lg:items-start">
            <div>
              {introParagraphs.map((paragraph, index) => (
                <p key={index} className="text-lg mb-6" style={{ color: colors.text.primary }}>
                  {paragraph}
                </p>
              ))}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {introFeatures.map((feature, index) => (
                  <InteractiveCard key={index} className="p-4" variant="glass">
                    <div className="flex items-start space-x-3">
                      <div
                        className="flex-shrink-0 p-2 rounded-lg"
                        style={{ backgroundColor: colors.accent.primary + '20' }}
                      >
                        <div style={{ color: colors.accent.primary }}>
                          {renderFeatureIcon(feature.icon)}
                        </div>
                      </div>
                      <div>
                        <h3 className="font-semibold mb-1" style={{ color: colors.text.primary }}>
                          {feature.title}
                        </h3>
                        <p className="text-sm" style={{ color: colors.text.primary }}>
                          {feature.description}
                        </p>
                      </div>
                    </div>
                  </InteractiveCard>
                ))}
              </div>
            </div>

            <div className="relative">
              <LazyVideo
                src={introVideoSrc}
                poster={introPoster}
                autoPlay
                loop
                muted
                playsInline
                preload="metadata"
                className="w-full h-full"
                videoClassName="rounded-lg shadow-xl w-full h-full object-cover"
                pauseWhenOutOfView
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent rounded-lg"></div>
            </div>
          </div>
        </div>
      </section>

      <section data-cms-section="package7day" data-cms-page="packages" id="package-7day" className="py-16" style={{ backgroundColor: colors.secondary }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold mb-4" style={{ color: colors.text.primary }}>
              <Star className="inline h-8 w-8 mr-3" style={{ color: colors.accent.primary }} />
              {featured.title || t('pages.laplandHoliday.featured.title')}
            </h2>
            <p className="text-xl" style={{ color: colors.text.primary }}>
              {featured.subtitle || t('pages.laplandHoliday.featured.subtitle')}
            </p>
            <div
              className="inline-block px-4 py-2 rounded-full text-sm font-bold mt-4"
              style={{ backgroundColor: colors.accent.primary, color: colors.text.primary }}
            >
              {featured.badge || t('pages.laplandHoliday.featured.badge')}
            </div>
          </div>

          <InteractiveCard className="p-8 mb-8" variant="glass">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center mb-4">
                  <Gift className="h-8 w-8 mr-3" style={{ color: colors.accent.primary }} />
                  <div>
                    <h3 className="text-2xl font-bold" style={{ color: colors.text.primary }}>
                      {featuredPackage.name || t('pages.laplandHoliday.featured.package.name')}
                    </h3>
                    <p className="text-sm" style={{ color: colors.text.primary }}>
                      {featuredPackage.duration || t('pages.laplandHoliday.featured.package.duration')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center mb-6">
                  <span className="text-4xl font-bold" style={{ color: colors.text.primary }}>
                    {formatPrice(featuredPackage.price, formatEUR(2250))}
                  </span>
                  <span className="text-lg ml-2" style={{ color: colors.text.secondary }}>
                    {featuredPackage.perPerson || t('pages.laplandHoliday.featured.package.perPerson')}
                  </span>
                </div>

                <p className="text-lg mb-6" style={{ color: colors.text.primary }}>
                  {featuredPackage.description || t('pages.laplandHoliday.featured.package.description')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <PrimaryButton to={`${langPrefix}/book`} size="large">
                    {featuredPackage.primaryCta || t('pages.laplandHoliday.featured.package.primaryCta')}
                  </PrimaryButton>
                  <SecondaryButton to={`${langPrefix}/contact`} size="large">
                    {featuredPackage.secondaryCta || t('pages.laplandHoliday.featured.package.secondaryCta')}
                  </SecondaryButton>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-4" style={{ color: colors.accent.primary }}>
                  <Star className="inline h-5 w-5 mr-2" />
                  {featuredPackage.includesTitle || t('pages.laplandHoliday.featured.package.includesTitle')}
                </h4>
                <ul className="space-y-2">
                  {featuredIncludes.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: colors.accent.primary }} />
                      <span className="text-sm" style={{ color: colors.text.primary }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </InteractiveCard>

          <div className="grid md:grid-cols-3 gap-4">
            {[
              featured.media?.galleryVideo0 || "/optimized_videos/hundspann2-opt.mp4",
              featured.media?.galleryVideo1 || "/optimized_videos/snoskoter-opt.mp4",
              featured.media?.galleryVideo2 || "/optimized_videos/norrsken2-opt.mp4",
            ].map((videoSrc, index) => (
              <div key={videoSrc} className="relative">
                <LazyVideo
                  src={videoSrc}
                  poster={featured.media?.galleryPoster || "/og/packages.jpg"}
                  autoPlay
                  loop
                  muted
                  playsInline
                  preload="metadata"
                  className="block"
                  videoClassName="rounded-lg shadow-lg w-full h-48 object-cover"
                  pauseWhenOutOfView
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent rounded-lg flex items-end">
                  <p className="text-white font-semibold p-4">
                    {featuredGallery[index] || t(`pages.laplandHoliday.featured.galleryCaptions.${index}`)}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section data-cms-section="package5day" data-cms-page="packages" id="package-5day" className="py-16" style={{ background: colors.gradients.heroSolid }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ color: colors.text.primary }}>
              {fiveDay.title || t('pages.laplandHoliday.fiveDay.title')}
            </h2>
            <p className="text-lg" style={{ color: colors.text.primary }}>
              {fiveDay.subtitle || t('pages.laplandHoliday.fiveDay.subtitle')}
            </p>
          </div>

          <InteractiveCard className="p-8" variant="glass">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center mb-4">
                  <Calendar className="h-8 w-8 mr-3" style={{ color: colors.accent.primary }} />
                  <div>
                    <h3 className="text-2xl font-bold" style={{ color: colors.text.primary }}>
                      {fiveDayPackage.name || t('pages.laplandHoliday.fiveDay.package.name')}
                    </h3>
                    <p className="text-sm" style={{ color: colors.text.primary }}>
                      {fiveDayPackage.duration || t('pages.laplandHoliday.fiveDay.package.duration')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center mb-6">
                  <span className="text-4xl font-bold" style={{ color: colors.text.primary }}>
                    {formatPrice(fiveDayPackage.price, formatEUR(1750))}
                  </span>
                  <span className="text-lg ml-2" style={{ color: colors.text.secondary }}>
                    {fiveDayPackage.perPerson || t('pages.laplandHoliday.fiveDay.package.perPerson')}
                  </span>
                </div>

                <p className="text-lg mb-6" style={{ color: colors.text.primary }}>
                  {fiveDayPackage.description || t('pages.laplandHoliday.fiveDay.package.description')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <PrimaryButton to={`${langPrefix}/book`} size="large">
                    {fiveDayPackage.primaryCta || t('pages.laplandHoliday.fiveDay.package.primaryCta')}
                  </PrimaryButton>
                  <SecondaryButton to={`${langPrefix}/contact`} size="large">
                    {fiveDayPackage.secondaryCta || t('pages.laplandHoliday.fiveDay.package.secondaryCta')}
                  </SecondaryButton>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-4" style={{ color: colors.accent.primary }}>
                  <Star className="inline h-5 w-5 mr-2" />
                  {fiveDayPackage.includesTitle || t('pages.laplandHoliday.fiveDay.package.includesTitle')}
                </h4>
                <ul className="space-y-2">
                  {fiveDayIncludes.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: colors.accent.primary }} />
                      <span className="text-sm" style={{ color: colors.text.primary }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </InteractiveCard>
        </div>
      </section>

      <section data-cms-section="package3day" data-cms-page="packages" id="package-3day" className="py-16" style={{ backgroundColor: colors.primary }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ color: colors.text.primary }}>
              {threeDay.title || t('pages.laplandHoliday.threeDay.title')}
            </h2>
            <p className="text-lg" style={{ color: colors.text.primary }}>
              {threeDay.subtitle || t('pages.laplandHoliday.threeDay.subtitle')}
            </p>
          </div>

          <InteractiveCard className="p-8" variant="glass">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center mb-4">
                  <Calendar className="h-8 w-8 mr-3" style={{ color: colors.accent.primary }} />
                  <div>
                    <h3 className="text-2xl font-bold" style={{ color: colors.text.primary }}>
                      {threeDayPackage.name || t('pages.laplandHoliday.threeDay.package.name')}
                    </h3>
                    <p className="text-sm" style={{ color: colors.text.primary }}>
                      {threeDayPackage.duration || t('pages.laplandHoliday.threeDay.package.duration')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center mb-6">
                  <span className="text-4xl font-bold" style={{ color: colors.text.primary }}>
                    {formatPrice(threeDayPackage.price, formatEUR(1250))}
                  </span>
                  <span className="text-lg ml-2" style={{ color: colors.text.secondary }}>
                    {threeDayPackage.perPerson || t('pages.laplandHoliday.threeDay.package.perPerson')}
                  </span>
                </div>

                <p className="text-lg mb-6" style={{ color: colors.text.primary }}>
                  {threeDayPackage.description || t('pages.laplandHoliday.threeDay.package.description')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <PrimaryButton to={`${langPrefix}/book`} size="large">
                    {threeDayPackage.primaryCta || threeDayPackage.bookButton || t('pages.laplandHoliday.threeDay.package.primaryCta')}
                  </PrimaryButton>
                  <SecondaryButton to={`${langPrefix}/contact`} size="large">
                    {threeDayPackage.secondaryCta || threeDayPackage.askButton || t('pages.laplandHoliday.threeDay.package.secondaryCta')}
                  </SecondaryButton>
                </div>

                  {threeDayPackage.cancellationPolicy && (
                    <p className="text-sm mt-4" style={{ color: colors.text.secondary }}>
                      {t('pages.laplandHoliday.threeDay.package.cancellationPolicy')}
                    </p>
                  )}
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-4" style={{ color: colors.accent.primary }}>
                  <CheckCircle className="inline h-5 w-5 mr-2" style={{ color: colors.accent.primary }} />
                  {threeDayPackage.includesTitle || t('pages.laplandHoliday.threeDay.package.includesTitle')}
                </h4>
                <ul className="space-y-2">
                  {threeDayIncludes.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle
                        className="h-5 w-5 mt-0.5 flex-shrink-0"
                        style={{ color: colors.accent.primary }}
                      />
                      <span className="text-sm" style={{ color: colors.text.primary }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>

                  {threeDayItinerary.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-lg font-semibold mb-3" style={{ color: colors.accent.primary }}>
                        <Calendar className="inline h-5 w-5 mr-2" />
                        {threeDay.itineraryTitle || threeDayPackage.itineraryTitle || t('pages.laplandHoliday.threeDay.package.itineraryTitle')}
                      </h4>
                      <ul className="space-y-2 text-sm" style={{ color: colors.text.primary }}>
                        {threeDayItinerary.map((item, index) => (
                          <li key={index}>{t(`pages.laplandHoliday.threeDay.package.itinerary.${index}`)}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {threeDayAddOns.length > 0 && (
                    <div className="mt-6">
                      <h4 className="text-lg font-semibold mb-3" style={{ color: colors.accent.primary }}>
                        <Gift className="inline h-5 w-5 mr-2" />
                        {threeDay.addOnsTitle || threeDayPackage.addOnsTitle || t('pages.laplandHoliday.threeDay.package.addOnsTitle')}
                      </h4>
                      <ul className="space-y-2">
                        {threeDayAddOns.map((item, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <CheckCircle
                              className="h-5 w-5 mt-0.5 flex-shrink-0"
                              style={{ color: colors.accent.primary }}
                            />
                            <span className="text-sm" style={{ color: colors.text.primary }}>
                              {t(`pages.laplandHoliday.threeDay.package.addOns.${index}`)}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
              </div>
            </div>
          </InteractiveCard>
        </div>
      </section>

      <section data-cms-section="package1day" data-cms-page="packages" id="package-1day" className="py-16" style={{ backgroundColor: colors.primary }}>
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4" style={{ color: colors.text.primary }}>
              {oneDay.title || t('pages.laplandHoliday.oneDay.title')}
            </h2>
            <p className="text-lg" style={{ color: colors.text.primary }}>
              {oneDay.subtitle || t('pages.laplandHoliday.oneDay.subtitle')}
            </p>
          </div>

          <InteractiveCard className="p-8" variant="glass">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="flex items-center mb-4">
                  <Clock className="h-8 w-8 mr-3" style={{ color: colors.accent.primary }} />
                  <div>
                    <h3 className="text-2xl font-bold" style={{ color: colors.text.primary }}>
                      {oneDayPackage.name || t('pages.laplandHoliday.oneDay.package.name')}
                    </h3>
                    <p className="text-sm" style={{ color: colors.text.primary }}>
                      {oneDayPackage.duration || t('pages.laplandHoliday.oneDay.package.duration')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center mb-6">
                  <span className="text-4xl font-bold" style={{ color: colors.text.primary }}>
                    {formatPrice(oneDayPackage.price, formatEUR(380))}
                  </span>
                  <span className="text-lg ml-2" style={{ color: colors.text.secondary }}>
                    {oneDayPackage.perPerson || t('pages.laplandHoliday.oneDay.package.perPerson')}
                  </span>
                </div>

                <p className="text-lg mb-6" style={{ color: colors.text.primary }}>
                  {oneDayPackage.description || t('pages.laplandHoliday.oneDay.package.description')}
                </p>

                <div className="flex flex-col sm:flex-row gap-4">
                  <PrimaryButton to={`${langPrefix}/book`} size="large">
                    {oneDayPackage.primaryCta || t('pages.laplandHoliday.oneDay.package.primaryCta')}
                  </PrimaryButton>
                  <SecondaryButton to={`${langPrefix}/contact`} size="large">
                    {oneDayPackage.secondaryCta || t('pages.laplandHoliday.oneDay.package.secondaryCta')}
                  </SecondaryButton>
                </div>
              </div>

              <div>
                <h4 className="text-lg font-semibold mb-4" style={{ color: colors.accent.primary }}>
                  <Clock className="inline h-5 w-5 mr-2" />
                  {oneDayPackage.includesTitle || t('pages.laplandHoliday.oneDay.package.includesTitle')}
                </h4>
                <ul className="space-y-2">
                  {oneDayIncludes.map((item, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle className="h-5 w-5 mt-0.5 flex-shrink-0" style={{ color: colors.accent.primary }} />
                      <span className="text-sm" style={{ color: colors.text.primary }}>
                        {item}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </InteractiveCard>
        </div>
      </section>

      <section data-cms-section="packagesCta" data-cms-page="packages" id="packages-cta" className="py-16 text-center" style={{ backgroundColor: colors.secondary }}>
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-3xl font-bold mb-6" style={{ color: colors.text.primary }}>
            {cta.title || t('pages.laplandHoliday.cta.title')}
          </h2>
          <p className="text-xl mb-8" style={{ color: colors.text.primary }}>
            {cta.subtitle || t('pages.laplandHoliday.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <PrimaryButton to={`${langPrefix}/book`} size="large">
              {cta.primaryCta || t('pages.laplandHoliday.cta.primaryCta')}
            </PrimaryButton>
            <SecondaryButton to={`${langPrefix}/contact`} size="large">
              {cta.secondaryCta || t('pages.laplandHoliday.cta.secondaryCta')}
            </SecondaryButton>
            <OutlineButton to={`${langPrefix}/gallery`} size="large">
              {cta.outlineCta || t('pages.laplandHoliday.cta.outlineCta')}
            </OutlineButton>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LaplandHolidayPackages;
